
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from "react";
import { Trophy, Gift, TrendingUp, Lock, Wallet } from "lucide-react";
import { useWallet } from "../../hooks/useWallet";
import { Button } from "../ui/Button";

export const RewardsView: React.FC = () => {
  const { isConnected, walletAddress, walletType } = useWallet();

  if (!isConnected) {
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-6 animate-fade-in">
            <div className="w-20 h-20 bg-stone-100 dark:bg-white/5 rounded-3xl flex items-center justify-center text-stone-400 dark:text-stone-700 shadow-inner">
                <Lock size={40} />
            </div>
            <div className="max-w-md space-y-2">
                <h2 className="font-serif text-3xl text-stone-900 dark:text-white font-bold">Rewards Locked</h2>
                <p className="text-stone-500 dark:text-stone-400">Connect your sovereign identity to unlock your Ghost rewards, affiliate earnings, and exclusive merchant perks.</p>
            </div>
            <Button variant="primary" size="lg" className="rounded-xl shadow-xl" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                Connect Identity <Wallet size={18} className="ml-2"/>
            </Button>
        </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
        <div className="bg-gradient-to-r from-stone-900 to-stone-800 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden shadow-2xl border border-white/5">
             <div className="absolute right-0 top-0 w-64 h-64 bg-nobel-gold opacity-10 rounded-full blur-[60px] translate-x-1/3 -translate-y-1/3"></div>
             
             <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-12">
                 <div className="text-center md:text-left">
                     <div className="inline-flex items-center gap-2 text-nobel-gold text-xs font-bold uppercase tracking-[0.2em] mb-4">
                        <Trophy size={14} /> Active Status
                     </div>
                     <h2 className="font-serif text-4xl md:text-6xl font-bold mb-4">Platinum Member</h2>
                     <p className="text-stone-400 max-w-sm text-sm md:text-base leading-relaxed">You are in the top 1% of earners. Enjoy <span className="text-nobel-gold font-bold">0% transaction fees</span> on all POS payments for the next 12 months.</p>
                     
                     <div className="mt-8 flex items-center gap-4 p-3 bg-white/5 rounded-xl border border-white/10 w-fit">
                        <div className="text-[10px] font-bold text-stone-500 uppercase tracking-widest">ID Verified</div>
                        <code className="text-xs text-stone-300 font-mono">{walletAddress?.slice(0, 8)}...{walletAddress?.slice(-8)}</code>
                     </div>
                 </div>
                 <div className="flex flex-col items-center">
                     <div className="w-40 h-40 rounded-full border-4 border-nobel-gold flex items-center justify-center bg-stone-900/50 backdrop-blur shadow-[0_0_50px_rgba(197,160,89,0.4)] relative">
                         <div className="absolute -top-2 -right-2 w-10 h-10 bg-nobel-gold rounded-full flex items-center justify-center text-black border-4 border-stone-900 shadow-lg">
                             <Trophy size={20} />
                         </div>
                         <div className="text-center">
                             <div className="text-4xl font-serif font-bold text-white tracking-tighter">4,250</div>
                             <div className="text-[10px] text-nobel-gold uppercase font-black tracking-widest mt-1">GHOST POINTS</div>
                         </div>
                     </div>
                 </div>
             </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-[#15171C] p-8 rounded-2xl border border-stone-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl flex items-center justify-center mb-6 shadow-inner"><Trophy size={24} /></div>
                <div className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Affiliate Stats</div>
                <div className="text-3xl font-serif text-stone-900 dark:text-white font-bold tracking-tight">$1,250.00</div>
                <div className="text-xs text-stone-500 mt-2 flex items-center gap-1.5 font-medium">
                    <TrendingUp size={14} className="text-emerald-500"/> +12% from last cycle
                </div>
            </div>
            <div className="bg-white dark:bg-[#15171C] p-8 rounded-2xl border border-stone-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-xl flex items-center justify-center mb-6 shadow-inner"><TrendingUp size={24} /></div>
                <div className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Next Unlock</div>
                <div className="text-3xl font-serif text-stone-900 dark:text-white font-bold tracking-tight">500 pts</div>
                <div className="text-xs text-stone-500 mt-2 font-medium">Until $50 GhostVoucher issuance</div>
            </div>
            <div className="bg-white dark:bg-[#15171C] p-8 rounded-2xl border border-stone-100 dark:border-white/5 shadow-sm hover:shadow-xl transition-all">
                <div className="w-12 h-12 bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 rounded-xl flex items-center justify-center mb-6 shadow-inner"><Gift size={24} /></div>
                <div className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Active Perks</div>
                <div className="text-3xl font-serif text-stone-900 dark:text-white font-bold tracking-tight">3 Sovereign</div>
                <div className="text-xs text-stone-500 mt-2 font-medium">Zero Fees, Concierge, API Priority</div>
            </div>
        </div>
        
        <div className="pt-8 border-t border-stone-100 dark:border-white/5 flex justify-center">
            <p className="text-[10px] text-stone-400 font-bold uppercase tracking-[0.3em] flex items-center gap-2">
                <Lock size={12} className="text-nobel-gold"/> Rewards are cryptographically secured via GhostOS
            </p>
        </div>
    </div>
  );
};
